import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { TERMS_VERSION, type LoginResponse } from '@koyomi/shared';
import { Button, Chip, Input, PageLoading } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import { formatMoney } from '../../lib/queries.ts';
import { LEGAL } from '../help/help-api.ts';
import './signup.css';

interface Plan { id: string; name: string; description: string | null; price: number; maxUsers: number; estimatedInteractions: number; estimatedAppointments: number; featured: boolean }
interface Config { selfService: boolean; turnstileSiteKey: string | null; trial: { days: number; estimatedInteractions: number | null }; plans: Plan[]; termsVersion: string }
interface Started { requestId: string; email: string; expiresInSec: number; resendInSec: number }
declare global { interface Window { turnstile?: { render: (el: HTMLElement, opts: Record<string, unknown>) => string; reset: (id?: string) => void } } }

/** Link do WhatsApp da Lia com o número conectado agora (GET /public/contact); null enquanto carrega ou sem WhatsApp conectado. */
function useLiaWhatsApp(): string | null {
  const contact = useQuery({ queryKey: ['public-contact'], queryFn: () => api<{ whatsapp: string | null }>('/public/contact', { global: true }), staleTime: 5 * 60_000 });
  return contact.data?.whatsapp ? `https://wa.me/${contact.data.whatsapp}?text=${encodeURIComponent('Oi! Quero conhecer o Koyomi.')}` : null;
}

/** Cloudflare Turnstile (anti-robô): carrega o script uma vez e devolve o token quando o desafio passa. */
function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (t: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let id: string | undefined;
    const render = () => { if (ref.current && window.turnstile && !id) id = window.turnstile.render(ref.current, { sitekey: siteKey, callback: (t: string) => onToken(t), 'expired-callback': () => onToken(null), 'error-callback': () => onToken(null) }); };
    if (window.turnstile) render();
    else {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true; s.onload = render;
      document.head.appendChild(s);
    }
  }, [siteKey]);
  return <div ref={ref} className="signup__captcha" />;
}

/**
 * Cadastro pelo site (roadmap §3): plano → dados → código por e-mail → negócio em teste e sessão aberta; a Ativação abre
 * no assistente de primeira configuração. Só com o autoatendimento ligado no Admin.
 */
export function SignupPage() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const [params] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const cfg = useQuery({ queryKey: ['signup-config'], queryFn: () => api<Config>('/public/signup/config', { global: true }) });
  const whatsapp = useLiaWhatsApp();
  // Link curto da Lia (?c=...): o código leva o plano escolhido e a conversa de origem. ?ref= (token antigo) segue aceito.
  const linkCode = params.get('c');
  const link = useQuery({ queryKey: ['signup-link', linkCode], enabled: !!linkCode, queryFn: () => api<{ valid: boolean; planId: string | null }>(`/public/signup/link/${encodeURIComponent(linkCode!)}`, { global: true }) });
  const [planId, setPlanId] = useState<string | null>(params.get('plan'));
  const [f, setF] = useState({ businessName: '', segment: '', name: params.get('nome') ?? '', email: '', phone: params.get('tel') ?? '', password: '', acceptTerms: false });
  const [token, setToken] = useState<string | null>(null);
  const [started, setStarted] = useState<Started | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  useEffect(() => { if (wait <= 0) return; const id = setTimeout(() => setWait(wait - 1), 1000); return () => clearTimeout(id); }, [wait]);
  const c = cfg.data;
  useEffect(() => { if (c && (!planId || !c.plans.some((p) => p.id === planId))) setPlanId(c.plans.find((p) => p.featured)?.id ?? c.plans[0]?.id ?? null); }, [c]);
  useEffect(() => { const p = link.data?.planId; if (p && c?.plans.some((x) => x.id === p)) setPlanId(p); }, [link.data, c]);

  // Código errado: a mensagem do servidor diz quantas tentativas restam.
  const msg = (err: unknown) => err instanceof ApiError ? (err.code === 'SIGNUP_CODE_INVALID' || err.code === 'RATE_LIMITED' ? err.message : t(`common:errors.${err.code}`, { defaultValue: '' }) || err.message) : t('common:errors.INTERNAL');
  const start = async () => {
    setBusy(true); setError(null);
    try {
      const utm = Object.fromEntries([...params.entries()].filter(([k]) => k.startsWith('utm_')));
      const r = await api<Started>('/auth/signup/start', { global: true, body: { planId, ...f, segment: f.segment || null, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo', locale: i18n.language === 'en-US' ? 'en-US' : 'pt-BR', turnstileToken: token ?? undefined, ref: linkCode ?? params.get('ref') ?? undefined, utm: Object.keys(utm).length ? utm : undefined } });
      setStarted(r); setWait(r.resendInSec);
    } catch (err) { setError(msg(err)); window.turnstile?.reset(); setToken(null); } finally { setBusy(false); }
  };
  const confirm = async () => {
    if (!started) return;
    setBusy(true); setError(null);
    try {
      const r = await api<LoginResponse & { businessId: string }>('/auth/signup/confirm', { global: true, body: { requestId: started.requestId, code } });
      useAuthStore.getState().setBusiness(r.businessId);
      setSession(r); // a rota anônima redireciona e a Ativação abre no assistente
    } catch (err) { setError(msg(err)); } finally { setBusy(false); }
  };
  const resend = async () => {
    if (!started) return;
    try { const r = await api<{ resendInSec: number }>('/auth/signup/resend', { global: true, body: { requestId: started.requestId } }); setWait(r.resendInSec); setCode(''); setError(null); } catch (err) { setError(msg(err)); }
  };

  if (cfg.isLoading) return <div className="signup"><PageLoading cards={2} rows={4} /></div>;
  if (!c?.selfService) {
    return (
      <div className="signup signup--narrow">
        <div className="signup__brand">{t('common:appName')}</div>
        <h1>{t('auth:signup.offTitle')}</h1>
        <p className="k-muted">{t('auth:signup.offHint')}</p>
        <div className="k-row" style={{ gap: 8 }}>{whatsapp && <a className="k-btn k-btn--primary" href={whatsapp}>{t('auth:signup.whatsapp')}</a>}<Link className="k-btn" to="/login">{t('auth:signup.login')}</Link></div>
      </div>
    );
  }
  const valid = !!planId && f.businessName.trim().length >= 2 && f.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(f.email) && f.phone.replace(/\D/g, '').length >= 10 && f.password.length >= 8 && f.acceptTerms && (!c.turnstileSiteKey || !!token);

  if (started) {
    return (
      <div className="signup signup--narrow">
        <div className="signup__brand">{t('common:appName')}</div>
        <h1>{t('auth:signup.codeTitle')}</h1>
        <p className="k-muted">{t('auth:signup.codeHint', { email: started.email })}</p>
        <Input label={t('auth:signup.code')} inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) void confirm(); }} />
        {error && <div className="k-field__error" role="alert">{error}</div>}
        <Button variant="primary" size="lg" block loading={busy} disabled={code.length !== 6} onClick={() => void confirm()}>{t('auth:signup.confirm')}</Button>
        <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" size="sm" disabled={wait > 0} onClick={() => void resend()}>{wait > 0 ? t('auth:signup.resendIn', { s: wait }) : t('auth:signup.resend')}</Button>
          <Button variant="ghost" size="sm" onClick={() => { setStarted(null); setCode(''); setError(null); }}>{t('auth:signup.back')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="signup">
      <div className="signup__brand">{t('common:appName')}</div>
      <h1>{t('auth:signup.title')}</h1>
      <p className="k-muted">{t('auth:signup.subtitle', { days: c.trial.days })}</p>
      <div className="signup__plans" role="radiogroup" aria-label={t('auth:signup.plan')}>
        {c.plans.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={planId === p.id} className={`signup__plan ${planId === p.id ? 'signup__plan--on' : ''}`} onClick={() => setPlanId(p.id)}>
            <span className="k-row" style={{ justifyContent: 'space-between', gap: 8 }}><strong>{p.name}</strong>{p.featured && <Chip tone="accent">{t('auth:signup.featured')}</Chip>}</span>
            <span className="signup__price">{formatMoney(p.price, i18n.language)}<small>/{t('auth:signup.month')}</small></span>
            <span className="k-muted">{t('auth:signup.planLine', { users: p.maxUsers, interactions: p.estimatedInteractions, appointments: p.estimatedAppointments })}</span>
            {p.description && <span className="k-muted" style={{ fontSize: 13 }}>{p.description}</span>}
          </button>
        ))}
      </div>
      <p className="k-muted" style={{ fontSize: 12 }}>{t('auth:signup.estimateNote', { days: c.trial.days })}</p>
      <div className="signup__form">
        <Input label={t('auth:signup.businessName')} value={f.businessName} onChange={(e) => setF({ ...f, businessName: e.target.value })} autoComplete="organization" />
        <Input label={t('auth:signup.segment')} placeholder={t('auth:signup.segmentPlaceholder')} value={f.segment} onChange={(e) => setF({ ...f, segment: e.target.value })} />
        <Input label={t('auth:signup.name')} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoComplete="name" />
        <Input label={t('auth:signup.phone')} hint={t('auth:signup.phoneHint')} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} inputMode="tel" autoComplete="tel" />
        <Input label={t('auth:signup.email')} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoComplete="email" />
        <Input label={t('auth:signup.password')} hint={t('auth:signup.passwordHint')} type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" />
      </div>
      <label className="signup__terms"><input type="checkbox" checked={f.acceptTerms} onChange={(e) => setF({ ...f, acceptTerms: e.target.checked })} /> <span>{t('auth:signup.terms', { version: TERMS_VERSION })}</span></label>
      <p className="k-muted signup__legal"><a href={LEGAL.terms} target="_blank" rel="noopener noreferrer">{t('common:help.terms')}</a> · <a href={LEGAL.privacy} target="_blank" rel="noopener noreferrer">{t('common:help.privacy')}</a></p>
      {c.turnstileSiteKey && <Turnstile siteKey={c.turnstileSiteKey} onToken={setToken} />}
      {error && <div className="k-field__error" role="alert">{error}</div>}
      <Button variant="primary" size="lg" block loading={busy} disabled={!valid} onClick={() => void start()}>{t('auth:signup.submit')}</Button>
      <p className="k-muted" style={{ textAlign: 'center', fontSize: 14 }}>{t('auth:signup.haveAccount')} <Link to="/login">{t('auth:signup.login')}</Link>{whatsapp && <> · <a href={whatsapp}>{t('auth:signup.specialist')}</a></>}</p>
    </div>
  );
}
